import type { Dispatch } from "react"

import { LayoutGroup, MotionConfig } from "motion/react"
import { useTranslation } from "react-i18next"

import type { SegmentAnalysisAction } from "../hooks/use-segment-analysis"
import type {
  Segment,
  SegmentAnalysisDataSource,
  SegmentCustomerFilter,
  SegmentReport,
  SegmentReportEntity,
} from "../types"
import type { EntityListItem } from "./entity-list"

import { Search } from "@/components/icons"

import { EntityList } from "./entity-list"
import { SectionCard } from "./segment-detail-primitives"
import { useCcpSummaries } from "../hooks/use-ccp-summaries"
import { COMPANY_NAME } from "../utils/branding"

/** Per-user company-favourite controls + view filters threaded into the people tabs (§G2). */
export type EntityViewProps = {
  customerFilter: SegmentCustomerFilter
  entitySearch: string
  favouriteCompanies: Set<string>
  canFavourite: boolean
  onToggleFavourite: (companyName: string) => void
  /** Persisted customer↔prospect overrides (reclassify). Keyed by company name. */
  companyClassifications?: Record<string, "customer" | "prospect">
  /** Reclassify a company customer↔prospect (edit-gated); omit to hide the control. */
  onReclassify?: (companyName: string, next: "customer" | "prospect") => void
}

type EntityRow = EntityListItem

function toRows(
  entities: SegmentReportEntity[],
  descriptionKey: "changeSummary" | "rationale",
  defaultClassification?: "customer" | "prospect"
): EntityRow[] {
  return entities.map((entity) => ({
    title: entity.name,
    description: entity[descriptionKey] ?? "",
    flag: entity.flag,
    salesPotential: entity.salesPotential,
    assumedRevenue: entity.assumedRevenue,
    assumedGrowth: entity.assumedGrowth,
    classification: entity.classification ?? defaultClassification,
  }))
}

function matchesSearch(rows: EntityRow[], search: string): EntityRow[] {
  const needle = search.trim().toLowerCase()
  if (!needle) return rows
  return rows.filter((row) => row.title.toLowerCase().includes(needle))
}

function EntitySearch({
  dispatch,
  placeholder,
  value,
}: {
  dispatch: Dispatch<SegmentAnalysisAction>
  placeholder: string
  value: string
}) {
  return (
    <div className="relative max-w-sm">
      <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
      <input
        aria-label={placeholder}
        className="border-input bg-background text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/15 h-9 w-full rounded-md border pr-3 pl-8 text-sm shadow-sm transition-[box-shadow,border-color] duration-200 outline-none focus-visible:ring-[3px] focus-visible:outline-none"
        placeholder={placeholder}
        type="search"
        value={value}
        onChange={(event) => dispatch({ type: "setEntitySearch", value: event.target.value })}
      />
    </div>
  )
}

function favouriteProps(entityView: EntityViewProps) {
  return entityView.canFavourite
    ? {
        isFavourite: (name: string) => entityView.favouriteCompanies.has(name),
        onToggleFavourite: entityView.onToggleFavourite,
      }
    : {}
}

export type OpenCcpOptions = { autoDraft?: boolean }

export function CustomersTab({
  dataSource,
  dispatch,
  entityView,
  report,
  segment,
  onAnalyzeCompany,
}: {
  dataSource?: SegmentAnalysisDataSource
  dispatch: Dispatch<SegmentAnalysisAction>
  entityView: EntityViewProps
  report: SegmentReport | null
  segment: Segment
  /** Opens the customer's Customer Centric Plan (CCP). Prospects pass autoDraft. */
  onAnalyzeCompany?: (companyName: string, options?: OpenCcpOptions) => void
}) {
  const { t } = useTranslation()
  const existing =
    report?.customerAnalysis.existingKeyCustomers ??
    segment.customers.map((name) => ({
      name,
      changeSummary: "Run a full or partial report to refresh customer evidence.",
    }))
  const prospects = report?.customerAnalysis.potentialNewCustomers ?? []
  const { entitySearch } = entityView

  // When backed by live data, resolve each customer/prospect's CCP summary blurb and
  // let it override the (mock-ish) card description. Best-effort — no blurb, no change.
  const entityNames = [...existing, ...prospects].map((entity) => entity.name)
  const ccpSummaries = useCcpSummaries(segment.id, entityNames, dataSource !== "mock")

  // Re-bucket by EFFECTIVE classification so a reclassified company moves between
  // Customers and Prospects (override wins over the section it came from).
  const overrides = entityView.companyClassifications ?? {}
  const allRows = [
    ...toRows(existing, "changeSummary", "customer"),
    ...toRows(prospects, "rationale", "prospect"),
  ].map((row) => ({
    ...row,
    classification: overrides[row.title] ?? row.classification,
    description: ccpSummaries[row.title] ?? row.description,
  }))

  const existingRows = matchesSearch(
    allRows.filter((row) => row.classification !== "prospect"),
    entitySearch
  )
  const prospectRows = matchesSearch(
    allRows.filter((row) => row.classification === "prospect"),
    entitySearch
  )
  const fav = favouriteProps(entityView)
  const reclassify = entityView.onReclassify ? { onReclassify: entityView.onReclassify } : {}

  return (
    <div className="grid gap-4">
      <div className="flex justify-end">
        <EntitySearch dispatch={dispatch} placeholder="Search customers" value={entitySearch} />
      </div>
      {/* MotionConfig honours prefers-reduced-motion; the LayoutGroup scopes the
          shared layoutIds so a reclassified row glides between the two cards. */}
      <MotionConfig reducedMotion="user">
        <LayoutGroup id={`segment-customers-${segment.id}`}>
          <div className="grid gap-4 lg:grid-cols-2">
            <SectionCard title="Existing key customers">
              <EntityList
                {...fav}
                {...reclassify}
                animateLayout
                ctaLabel={t("segmentAnalysis.entity.dossierCta")}
                ctaTestId="sa-open-ccp"
                items={existingRows}
                emptyText={
                  entitySearch ? "No customers match your search." : "No customers recorded yet."
                }
                onAnalyze={onAnalyzeCompany}
              />
            </SectionCard>
            <SectionCard title="Suggested new prospects">
              <EntityList
                {...fav}
                {...reclassify}
                animateLayout
                ctaLabel={t("segmentAnalysis.entity.dossierCta")}
                ctaTestId="sa-open-ccp"
                items={prospectRows}
                emptyText={
                  entitySearch
                    ? "No prospects match your search."
                    : "Run a full report to discover prospects."
                }
                onAnalyze={
                  onAnalyzeCompany
                    ? (name) => onAnalyzeCompany(name, { autoDraft: true })
                    : undefined
                }
              />
            </SectionCard>
          </div>
        </LayoutGroup>
      </MotionConfig>
    </div>
  )
}

export function CompetitorsTab({
  dispatch,
  entityView,
  report,
  segment,
  onAnalyzeCompany,
}: {
  dispatch: Dispatch<SegmentAnalysisAction>
  entityView: EntityViewProps
  report: SegmentReport | null
  segment: Segment
  /** Opens the competitor's Competitor Plan (benchmarked vs the tenant company). */
  onAnalyzeCompany?: (companyName: string) => void
}) {
  const { t } = useTranslation()
  const competitors =
    report?.competitorAnalysis.mainCompetitors ??
    segment.competitors.map((name) => ({
      name,
      changeSummary: "Run a full or partial report to refresh competitor evidence.",
    }))
  const newEntrants = report?.competitorAnalysis.newEntrants ?? []
  const { entitySearch } = entityView

  const competitorRows = matchesSearch(toRows(competitors, "changeSummary"), entitySearch)
  const entrantRows = matchesSearch(toRows(newEntrants, "changeSummary"), entitySearch)
  const fav = favouriteProps(entityView)

  return (
    <div className="grid gap-4">
      <div className="flex justify-end">
        <EntitySearch dispatch={dispatch} placeholder="Search competitors" value={entitySearch} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <SectionCard title="Established competitors">
          <EntityList
            {...fav}
            ctaLabel={t("segmentAnalysis.entity.competitorPlanCta")}
            ctaTestId="sa-open-competitor-plan"
            items={competitorRows}
            emptyText={
              entitySearch ? "No competitors match your search." : "No competitors recorded yet."
            }
            onAnalyze={onAnalyzeCompany}
          />
        </SectionCard>
        <SectionCard title="Newly detected entrants">
          <EntityList
            {...fav}
            ctaLabel={t("segmentAnalysis.entity.competitorPlanCta")}
            ctaTestId="sa-open-competitor-plan"
            items={entrantRows}
            emptyText={
              entitySearch
                ? "No new entrants match your search."
                : "No new entrants detected in the latest run."
            }
            onAnalyze={onAnalyzeCompany}
          />
        </SectionCard>
      </div>
      <p className="text-muted-foreground text-xs">
        Open a competitor to review its Competitor Plan — an account-level view benchmarked against{" "}
        {COMPANY_NAME}, including strengths vs. weaknesses. Analysis is per competitor, not a
        portfolio-level roll-up.
      </p>
    </div>
  )
}
